import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Sparkles, RefreshCw, Trash2, Calendar, Mail, Phone, MapPin, MessageSquare, ClipboardList } from "lucide-react";
import { adminApi, formatEUR } from "@/lib/api";
import LiveChats from "@/components/LiveChats";

const STATUSES = [
    { key: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-900 border-yellow-300" },
    { key: "confirmed", label: "Confirmed", color: "bg-blue-100 text-blue-900 border-blue-300" },
    { key: "completed", label: "Completed", color: "bg-green-100 text-green-900 border-green-300" },
    { key: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-900 border-red-300" },
];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [view, setView] = useState("bookings");
    const [bookings, setBookings] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [emails, setEmails] = useState([]);

    useEffect(() => {
        if (!localStorage.getItem("ss_admin_token")) {
            navigate("/admin");
        } else {
            fetchAll();
        }
    }, [navigate]);

    useEffect(() => {
        if (!selected) { setEmails([]); return; }
        adminApi().get(`/admin/bookings/${selected.id}/emails`).then((r) => setEmails(r.data)).catch(() => setEmails([]));
    }, [selected]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [b, s] = await Promise.all([
                adminApi().get("/admin/bookings"),
                adminApi().get("/admin/stats"),
            ]);
            setBookings(b.data);
            setStats(s.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("ss_admin_token");
                navigate("/admin");
            }
            toast.error("Failed to load bookings");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("ss_admin_token");
        navigate("/admin");
    };

    const updateStatus = async (id, status) => {
        try {
            await adminApi().patch(`/admin/bookings/${id}`, { status });
            toast.success(`Marked as ${status}`);
            fetchAll();
        } catch {
            toast.error("Failed to update");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this booking permanently?")) return;
        try {
            await adminApi().delete(`/admin/bookings/${id}`);
            toast.success("Deleted");
            setSelected(null);
            fetchAll();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const visible = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-cream-light">
            {/* Top bar */}
            <header className="bg-navy text-cream border-b border-gold/30 px-6 md:px-10 py-4 flex items-center justify-between sticky top-0 z-40">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-gold" />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-script text-2xl text-gold -mb-1">Signature</span>
                        <span className="font-playfair text-[10px] tracking-[0.3em] uppercase">Admin Dashboard</span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        data-testid="admin-refresh-btn"
                        onClick={fetchAll}
                        className="border border-gold/40 text-cream px-4 py-2 text-sm uppercase tracking-wider font-outfit hover:bg-gold/10 inline-flex items-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="bg-gold text-navy px-4 py-2 text-sm uppercase tracking-wider font-outfit font-semibold inline-flex items-center gap-2 hover:bg-gold-light"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </header>

            <main className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
                {/* View tabs */}
                <div className="flex gap-2 mb-8 border-b border-cream-border">
                    <button
                        data-testid="view-bookings-tab"
                        onClick={() => setView("bookings")}
                        className={`px-5 py-3 text-xs uppercase tracking-wider font-outfit font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${view === "bookings" ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-navy"}`}
                    >
                        <ClipboardList className="w-4 h-4" /> Bookings
                    </button>
                    <button
                        data-testid="view-chats-tab"
                        onClick={() => setView("chats")}
                        className={`px-5 py-3 text-xs uppercase tracking-wider font-outfit font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${view === "chats" ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-navy"}`}
                    >
                        <MessageSquare className="w-4 h-4" /> Live Chats
                    </button>
                </div>

                {view === "chats" ? (
                    <LiveChats />
                ) : (
                <>
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                        <StatCard label="Total" value={stats.total} testid="stat-total" />
                        <StatCard label="Pending" value={stats.pending} testid="stat-pending" />
                        <StatCard label="Confirmed" value={stats.confirmed} testid="stat-confirmed" />
                        <StatCard label="Completed" value={stats.completed} testid="stat-completed" />
                        <StatCard label="Revenue" value={formatEUR(stats.revenue)} testid="stat-revenue" highlight />
                    </div>
                )}

                {/* Filter tabs */}
                <div className="flex flex-wrap gap-2 mb-6 border-b border-cream-border pb-4">
                    {["all", ...STATUSES.map((s) => s.key)].map((f) => (
                        <button
                            key={f}
                            data-testid={`filter-${f}`}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 text-xs uppercase tracking-wider font-outfit border ${
                                filter === f ? "border-navy bg-navy text-gold" : "border-cream-border text-navy hover:border-navy"
                            }`}
                        >
                            {f === "all" ? `All (${bookings.length})` : `${f} (${bookings.filter((b) => b.status === f).length})`}
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="bg-white border border-cream-border overflow-x-auto">
                    <table className="w-full text-sm font-outfit">
                        <thead>
                            <tr className="bg-navy text-gold uppercase text-xs tracking-wider">
                                <th className="px-4 py-3 text-left">Date / Time</th>
                                <th className="px-4 py-3 text-left">Customer</th>
                                <th className="px-4 py-3 text-left">Service</th>
                                <th className="px-4 py-3 text-right">Total</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">Loading…</td></tr>
                            ) : visible.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground italic">No bookings yet.</td></tr>
                            ) : (
                                visible.map((b) => (
                                    <tr key={b.id} className="border-t border-cream-border hover:bg-cream-light cursor-pointer" onClick={() => setSelected(b)} data-testid={`booking-row-${b.id}`}>
                                        <td className="px-4 py-4">
                                            <div className="text-navy font-semibold">{b.date}</div>
                                            <div className="text-muted-foreground text-xs">{b.time_slot}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-navy">{b.full_name}</div>
                                            <div className="text-muted-foreground text-xs">{b.email}</div>
                                        </td>
                                        <td className="px-4 py-4 text-navy">{b.service_name}</td>
                                        <td className="px-4 py-4 text-right text-navy font-semibold">{formatEUR(b.quote_total)}</td>
                                        <td className="px-4 py-4">
                                            <StatusBadge status={b.status} />
                                        </td>
                                        <td className="px-4 py-4 text-right">
                                            <span className="text-xs text-gold-dark uppercase tracking-wider">View →</span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                </>
                )}
            </main>

            {/* Detail Drawer */}
            {selected && (
                <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-detail-drawer">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
                    <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto p-8 border-l border-gold">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-playfair text-2xl text-navy">Booking Details</h3>
                            <button data-testid="drawer-close-btn" onClick={() => setSelected(null)} className="text-muted-foreground hover:text-navy">✕</button>
                        </div>

                        <StatusBadge status={selected.status} large />
                        <div className="mt-6 font-outfit text-sm space-y-3 border-t border-cream-border pt-6">
                            <Line icon={Calendar} label="Date & Time" value={`${selected.date} @ ${selected.time_slot}`} />
                            <Line icon={Sparkles} label="Service" value={`${selected.service_name} • ${selected.bedrooms} bed / ${selected.bathrooms} bath`} />
                            <Line icon={Sparkles} label="Frequency" value={selected.frequency.replace("_", "-")} />
                            {selected.extras.length > 0 && <Line icon={Sparkles} label="Extras" value={selected.extras.join(", ")} />}
                            <Line icon={Mail} label="Email" value={selected.email} />
                            <Line icon={Phone} label="Phone" value={selected.phone} />
                            <Line icon={MapPin} label="Address" value={`${selected.address}, ${selected.city} ${selected.eircode}`} />
                            {selected.notes && <Line icon={Sparkles} label="Notes" value={selected.notes} />}
                        </div>

                        <div className="mt-6 p-4 bg-navy text-cream flex items-center justify-between">
                            <span className="uppercase tracking-wider text-xs text-gold">Total</span>
                            <span className="font-playfair text-2xl text-gold">{formatEUR(selected.quote_total)}</span>
                        </div>
                        {selected.deposit_amount > 0 && (
                            <div className={`mt-2 p-3 border flex items-center justify-between font-outfit text-sm ${selected.deposit_paid ? "border-green-300 bg-green-50 text-green-900" : "border-cream-border text-navy"}`}>
                                <span className="uppercase tracking-wider text-xs">Deposit (20%)</span>
                                <span className="font-semibold">{formatEUR(selected.deposit_amount)} • {selected.deposit_paid ? "Paid ✓" : "Pending"}</span>
                            </div>
                        )}

                        {/* Email delivery log */}
                        <div className="mt-8" data-testid="email-log-panel">
                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Email Delivery</label>
                            {emails.length === 0 ? (
                                <p className="text-xs text-muted-foreground italic">No emails dispatched yet.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {emails.map((e, i) => (
                                        <li key={i} className="border border-cream-border bg-white p-3 text-xs font-outfit">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="uppercase tracking-wider text-[10px] text-muted-foreground">{e.kind?.replace("_", " ")}</span>
                                                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${e.status === "sent" ? "bg-green-100 text-green-800" : e.status === "error" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                                                    {e.status}
                                                </span>
                                            </div>
                                            <div className="text-navy truncate">{e.subject}</div>
                                            <div className="text-muted-foreground text-[10px] mt-1">→ {e.to}</div>
                                            {e.error && <div className="text-red-700 text-[10px] mt-1 line-clamp-2">{e.error}</div>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="mt-8">
                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Update Status</label>
                            <div className="grid grid-cols-2 gap-2">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s.key}
                                        data-testid={`set-status-${s.key}`}
                                        onClick={() => updateStatus(selected.id, s.key)}
                                        className={`px-3 py-2 text-xs uppercase tracking-wider font-outfit border ${selected.status === s.key ? "border-gold bg-gold text-navy font-semibold" : "border-cream-border text-navy hover:border-gold"}`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button
                            data-testid="drawer-delete-btn"
                            onClick={() => remove(selected.id)}
                            className="mt-6 w-full border border-red-300 text-red-700 py-3 font-outfit text-sm uppercase tracking-wider hover:bg-red-50 inline-flex items-center justify-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" /> Delete Booking
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, testid, highlight }) {
    return (
        <div data-testid={testid} className={`p-5 border ${highlight ? "bg-navy text-cream border-gold" : "bg-white border-cream-border"}`}>
            <div className={`uppercase tracking-[0.2em] text-xs ${highlight ? "text-gold" : "text-muted-foreground"}`}>{label}</div>
            <div className={`font-playfair text-3xl mt-2 ${highlight ? "text-gold" : "text-navy"}`}>{value}</div>
        </div>
    );
}

function StatusBadge({ status, large }) {
    const s = STATUSES.find((x) => x.key === status);
    if (!s) return null;
    return (
        <span className={`inline-block ${s.color} border ${large ? "px-4 py-2 text-sm" : "px-2.5 py-1 text-xs"} uppercase tracking-wider font-outfit font-semibold`}>
            {s.label}
        </span>
    );
}

function Line({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" />
            <div>
                <div className="uppercase tracking-[0.15em] text-[10px] text-muted-foreground font-semibold">{label}</div>
                <div className="text-navy mt-0.5">{value}</div>
            </div>
        </div>
    );
}
